/**
 * Towers: the separate layer stacks that live inside one checkpoint.
 *
 * A decoder-only language model has exactly one stack, and `layers.7.` names a
 * single place in it. A multimodal checkpoint does not. Read off
 * `google/gemma-4-E4B-it-qat-q4_0-unquantized` (2076 tensors, one file), there
 * are three:
 *
 *     model.language_model.layers.0…41.…          665 tensors
 *     model.vision_tower.encoder.layers.0…15.…    658 tensors
 *     model.audio_tower.layers.0…11.…             751 tensors
 *
 * Each is numbered from zero, so **a layer index is only meaningful inside its
 * own stack**. Ordering the tensor list by that index alone interleaves the
 * three — audio 0, text 0, vision 0, audio 1, text 1, … — which is what the
 * owner saw and reported as "video / audio / text towers seem to be mixed on
 * each layer". Three unrelated blocks under three headings that all say `0` is
 * worse than no grouping at all.
 *
 * **The stack root is the evidence, not a keyword list.** Towers are discovered
 * structurally: whatever precedes the layer keyword is the root, and two
 * distinct roots are two stacks. That is a fact about the file, and it works on
 * an architecture nobody here has seen. Keywords do one narrower job on top of
 * it — turning `model.vision_tower.encoder.` into the word "vision" for a
 * button, and adopting the handful of *non*-stack tensors that plainly belong
 * to a tower (`model.embed_vision.embedding_projection.weight`) without needing
 * to be under its root.
 *
 * **Costs nothing.** Every input is a tensor name already read from the
 * safetensors header. No request is issued here, ever — see the crawling
 * invariant in CLAUDE.md.
 *
 * Pure: strings in, plain objects out, no DOM and no state.
 */

import { classifyTensor, layerGroupOf } from "./anatomy.js";

/** One layer stack, plus the label a filter button shows for it. */
export interface Tower {
  /**
   * Stable within one call, and what `data-tower` carries. Equal to `label`,
   * which `detectTowers` guarantees is unique across the returned set.
   */
  readonly id: string;
  /**
   * The stack root, trailing dot included — `model.vision_tower.encoder.`.
   * Empty for the synthetic shared tower, which has no stack of its own.
   */
  readonly root: string;
  /** Button text: a modality word where one was recognized, else the root's own. */
  readonly label: string;
  /** Set only when a modality keyword matched; drives the display order. */
  readonly modality: string | undefined;
}

/**
 * Modality words, in the order the buttons appear.
 *
 * Text first because the language stack is the model's backbone and the thing
 * most visitors came to look at; the rest follow in the order they are usually
 * bolted on. A fixed order rather than one derived from size or file position,
 * for the same reason `ROLE_ORDER` is fixed in `anatomy.ts`: two multimodal
 * models should lay out the same way so they can be compared by eye.
 *
 * Matching is on whole `_`/`-`-separated words inside one dotted segment, never
 * on substrings. `vision_tower` yields {vision, tower}; a tensor called
 * `revision_marker` yields {revision, marker} and matches nothing, which a
 * substring test would get wrong.
 */
const MODALITIES: ReadonlyArray<{
  readonly label: string;
  readonly words: readonly string[];
}> = [
  { label: "text", words: ["language", "text", "llm"] },
  { label: "vision", words: ["vision", "visual", "image", "vit"] },
  { label: "video", words: ["video"] },
  { label: "audio", words: ["audio", "speech", "conformer"] },
];

/**
 * Segments that name a container rather than a component.
 *
 * `model.` is the root of half the checkpoints on the Hub and labelling a
 * button "model" says nothing. `encoder` and `decoder` are deliberately *not*
 * here: on a T5 they are exactly what distinguishes the two stacks, and
 * suppressing them would leave both buttons unlabelled.
 */
const GENERIC_SEGMENTS: ReadonlySet<string> = new Set([
  "model",
  "base_model",
  "backbone",
  "transformer",
]);

/**
 * The bucket for tensors that belong to no single stack.
 *
 * Exported because callers have to be able to say "any tower but this one" —
 * the share card's §12.1 rule picks the largest *real* tower and must never
 * land on the synthetic bucket while a real one exists. A second copy of the
 * string in the og package would be a literal that drifts the first time this
 * one is renamed.
 */
export const SHARED_LABEL = "shared";

/**
 * The speculative-decoding draft block's tower.
 *
 * A real tower rather than a synthetic bucket — it has its own root and its own
 * tensors — but named for its function, because the alternative is a button
 * saying `blk.40` and a reader wondering which of a model's forty layers that
 * is. See `draftRoots` for why GGUF needs one at all.
 */
export const DRAFT_LABEL = "draft";

/** Whole words inside one dotted segment. `vision_tower` → `["vision","tower"]`. */
function wordsOf(segment: string): string[] {
  return segment.split(/[_-]/).filter(Boolean);
}

/** The modality a root announces, scanning specific → general. */
function modalityOfRoot(root: string): string | undefined {
  // Last segment first: `model.vision_tower.encoder.` should answer "vision"
  // from `vision_tower`, and the scan has to get past `encoder` to reach it —
  // but on a T5, where the root *is* `encoder.`, there is nothing further to
  // reach and the fallback label takes over.
  const segments = root.split(".").filter(Boolean).reverse();
  for (const segment of segments) {
    const words = new Set(wordsOf(segment));
    for (const modality of MODALITIES) {
      if (modality.words.some((word) => words.has(word))) return modality.label;
    }
  }
  return undefined;
}

/** What to call a root that announced no modality. */
function fallbackLabel(root: string): string {
  const segments = root.split(".").filter(Boolean);
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    const segment = segments[i];
    if (segment !== undefined && !GENERIC_SEGMENTS.has(segment)) return segment;
  }
  // Either a bare `h.0.` stack with no root at all, or a root made entirely of
  // container words. "main" is the honest reading of both: it is the stack the
  // others hang off.
  return "main";
}

/**
 * Distinct stack roots, in first-appearance order.
 *
 * First appearance and not sorted: the sort that matters is applied later by
 * modality, and file order is the only tie-break that carries any information
 * about the model.
 */
function stackRoots(names: readonly string[]): string[] {
  const seen = new Set<string>();
  const roots: string[] = [];
  for (const name of names) {
    const { layer, stackRoot } = layerGroupOf(name);
    if (layer === undefined) continue;
    if (seen.has(stackRoot)) continue;
    seen.add(stackRoot);
    roots.push(stackRoot);
  }
  return roots;
}

/**
 * The speculative-decoding draft block, which GGUF gives no namespace at all.
 *
 * ## Why a name rule and not a stack root
 *
 * A multi-token-prediction block is a second, one-layer model that predicts
 * ahead of the main one. Three spellings exist in the corpus and only the first
 * is handled by the machinery above:
 *
 * | spelling | repos | what happens without this |
 * | --- | --- | --- |
 * | `mtp.layers.N.…` | 2 | root `mtp.` — already its own tower, correct today |
 * | `mtp.N.…` | 5 (DeepSeek V4) | matches no layer pattern, falls to the global block |
 * | `blk.N.nextn.…` | 1 (llama.cpp) | **merged into the main stack as one more layer** |
 *
 * llama.cpp flattens the draft block into the main `blk.` numbering, so its
 * root is `""` — the same root as every other layer — and no amount of stack
 * analysis can separate it. `unsloth/Qwen3.6-35B-A3B-MTP-GGUF` therefore reads
 * as a **41-layer** model where the model has 40, with `blk.40` carrying a full
 * set of attention and feed-forward rows that belong to the draft.
 *
 * The only marker is the leaf: llama.cpp writes `nextn.eh_proj`, `nextn.enorm`,
 * `nextn.hnorm` and `nextn.shared_head_norm` in that block and nowhere else. So
 * the block that contains one is the draft block, and `blk.<i>.` becomes a root
 * of its own — which `towerOf` then prefers over `""`, because it is longer.
 *
 * Scoped to the block index rather than to the `nextn` tensors themselves, and
 * that is the whole point: the draft's attention and MLP carry ordinary names,
 * so moving only the marked tensors would leave the rest merged and the map
 * still claiming a 41st layer.
 */
const NEXTN_IN_BLOCK = /(?:^|\.)blk\.(\d+)\.nextn\./;

function draftRoots(names: readonly string[]): string[] {
  const seen = new Set<string>();
  for (const name of names) {
    const index = NEXTN_IN_BLOCK.exec(name)?.[1];
    if (index !== undefined) seen.add(`blk.${index}.`);
  }
  return [...seen];
}

/**
 * Find the towers in a set of tensor names.
 *
 * Returns `[]` for a model with one stack (or none) — a single-tower model must
 * not grow a row of filter buttons that does nothing, so "how many towers" and
 * "should the chrome exist" are the same question and get one answer.
 *
 * When there is more than one, a synthetic `shared` tower is appended if and
 * only if some name lands outside every real one. That is the multimodal
 * projector's natural home (`multi_modal_projector.linear_1.weight` belongs to
 * neither the vision stack nor the text stack, because its whole job is to join
 * them) and it keeps the buttons a partition: every tensor is under exactly one.
 */
/**
 * Does anything under this root turn tokens into vectors?
 *
 * Asked through `classifyTensor`, the anatomy's name-only path, rather than a
 * second embedding regex here. The map already paints `embed` as a first-class
 * role, so this is the one-source-of-truth rule applied to a fact the project
 * already owns — not a new classifier that could disagree with the grid a
 * visitor is looking at.
 */
function carriesEmbedding(root: string, names: readonly string[]): boolean {
  return names.some(
    (name) => name.startsWith(root) && classifyTensor(name).role === "embed",
  );
}

/**
 * Resolve same-modality contests before anything reads the list.
 *
 * ## The bug this removes
 *
 * `mlx-works/Qwen3.8-27B-oQ2e-mtp` is a whole 27.8B model whose index holds both
 * `language_model.model.` (1,844 tensors, carrying `embed_tokens`) and
 * `language_model.mtp.` (29 tensors). `modalityOfRoot` answers **text** for
 * both — the shared `language_model` segment decides it, because `mtp` matches
 * no modality word and the scan falls through. The first one seen took the
 * label; the other fell back to its raw root.
 *
 * Which one came first was the order the converter happened to write its weight
 * map. Measured both ways on a nine-tensor reduction:
 *
 *     body first → text = the body   default = 1,844 tensors
 *     head first → text = the HEAD   default =    29 tensors
 *
 * Same model, same code, different subject. On the real repo the head won, so
 * the page opened on `32 tensors (of 2,209) · 1.70B params (of 27.8B)` — and
 * the card was refused outright, because a one-layer stack trips
 * `MIN_CARD_LAYERS`. That is one of the eleven stuck keys.
 *
 * ## Why an ordering rather than a label fix
 *
 * The contest is decided **twice, from two different fields**: the label dedup
 * below resolves the *label*, and `defaultTower` selects on the *modality*
 * field, which the dedup never clears. `selectCardTower` and `towerOf`'s
 * adoption pass read it too. Reassigning the label would leave every one of
 * those still landing on the head (reviewer, 2026-08-31). Deciding the order
 * once, here, means every later reader inherits one answer instead of four
 * places re-deciding from a field nobody reconciled.
 *
 * ## Why the embedding and not size
 *
 * Size is a proxy for importance; the embedding is the thing itself. A stack
 * that cannot turn tokens into vectors is not the text tower however its
 * segments read, and a rule keyed on tensor counts hands the label to whichever
 * stack is bigger — which an MoE draft head can be. Plan §24's discipline: the
 * modality keyword is the name hint, the structure decides where they compete.
 *
 * Ties — both carry an embedding, or neither does — leave file order standing,
 * because at that point nothing structural distinguishes them and inventing a
 * winner would be the confident guess this exists to remove.
 */
function embeddingFirstWithinModality(
  roots: readonly string[],
  names: readonly string[],
): string[] {
  const ordered = [...roots];
  const positions = new Map<string, number[]>();
  ordered.forEach((root, index) => {
    const modality = modalityOfRoot(root);
    if (!modality) return;
    const seen = positions.get(modality);
    if (seen) seen.push(index);
    else positions.set(modality, [index]);
  });

  for (const slots of positions.values()) {
    if (slots.length < 2) continue;
    // Only the roots that contest this modality move, and only into the slots
    // they already occupy — everything else keeps its place, so a fix for one
    // collision cannot reorder an unrelated tower.
    const contenders = slots.map((i) => ordered[i] as string);
    const withEmbed = contenders.filter((r) => carriesEmbedding(r, names));
    const without = contenders.filter((r) => !carriesEmbedding(r, names));
    if (withEmbed.length === 0 || without.length === 0) continue;
    const resolved = [...withEmbed, ...without];
    slots.forEach((slot, i) => {
      ordered[slot] = resolved[i] as string;
    });
  }
  return ordered;
}

export function detectTowers(names: readonly string[]): Tower[] {
  const drafts = new Set(draftRoots(names));
  /*
   * Appended, not merged: a draft root is `blk.40.`, which no stack analysis
   * would ever produce, and it exists precisely because the model has only one
   * real stack root. On a GGUF with a draft block this takes `roots` from one
   * to two, which is also what turns the tower chrome on — correctly, since
   * there genuinely are two subjects to choose between.
   */
  const roots = embeddingFirstWithinModality([...stackRoots(names), ...drafts], names);
  if (roots.length < 2) return [];

  const towers: Tower[] = [];
  const taken = new Set<string>([SHARED_LABEL]);
  for (const root of roots) {
    const modality = modalityOfRoot(root);
    // Named for what it is rather than for its index: `blk.40` tells a reader
    // nothing, and the block's number is an artifact of where the converter
    // happened to append it.
    let label = drafts.has(root) ? DRAFT_LABEL : (modality ?? fallbackLabel(root));
    if (taken.has(label)) {
      // Two stacks that read the same — an encoder and a decoder both inside a
      // `vision_tower`, say. The root is the thing that actually differs, so
      // fall back to it rather than shipping two identical buttons.
      const full = root.replace(/\.$/, "");
      label = taken.has(full) ? `${label} (${towers.length})` : full || label;
    }
    taken.add(label);
    towers.push({ id: label, root, label, modality });
  }

  towers.sort((a, b) => rank(a) - rank(b));

  // Only now can "is anything left over" be answered, because it depends on the
  // whole tower set.
  if (names.some((name) => towerOf(name, towers) === undefined)) {
    towers.push({
      id: SHARED_LABEL,
      root: "",
      label: SHARED_LABEL,
      modality: undefined,
    });
  }
  return towers;
}

/**
 * Display order: unrecognized stacks first, then the modality order.
 *
 * Unrecognized first because the only common way to get one is a checkpoint
 * whose main stack sits at the bare root (`model.layers.N.` beside
 * `model.vision_tower.…`) — that stack is the backbone, and it should lead for
 * the same reason `text` does. `Array.prototype.sort` is stable, so several
 * unrecognized stacks keep file order between themselves.
 */
function rank(tower: Tower): number {
  if (tower.modality === undefined) return -1;
  return MODALITIES.findIndex((m) => m.label === tower.modality);
}

/**
 * Which tower a tensor belongs to, or `undefined` when there are no towers.
 *
 * Two passes, and the order between them is the whole correctness argument:
 *
 *  1. **Root containment, longest root wins.** `model.vision_tower.encoder.`
 *     and `model.` can both be prefixes of one name; the longer one is the
 *     nested stack and is the right answer. Matching the shorter first would
 *     file every vision tensor under the text tower on a Llava-shaped
 *     checkpoint, which is a *plausible-looking* wrong answer — the list would
 *     still render, just with the wrong contents under the wrong heading.
 *  2. **Modality words**, for the tensors that plainly belong to a tower while
 *     sitting outside its stack root. Gemma 4 keeps
 *     `model.vision_tower.patch_embedder.*` (outside `…encoder.`) and
 *     `model.embed_vision.*` (outside `model.vision_tower.` entirely); both
 *     read "vision" and both are useless anywhere else.
 *
 * A stack whose root is empty — GPT-2's `h.0.…` sitting beside a
 * `vision_model.encoder.layers.0.…` — is deliberately not part of pass 1, even
 * though `startsWith("")` would match it. It is the *fallback*: it takes what
 * nothing else claimed, after the modality words have had their say. Letting it
 * into pass 1 would make it swallow the whole model.
 *
 * Anything still unmatched is the shared tower's, if one exists.
 */
/**
 * The tower a model opens on — **text**, or the nearest thing it has to one.
 *
 * There is no "all" any more, and this function is why there can't be. A
 * multimodal checkpoint has several stacks each numbered from zero, so the pair
 * (layer, role) that keys the anatomy map is only unique *inside* one stack:
 * with every tower in one grid, audio layer 3, text layer 3 and vision layer 3
 * merge into one cell under one heading. "All" was not a wider view of the
 * model, it was three unrelated models added together — so the fix is to build
 * from one tower's tensors, where the collision cannot arise, and the mode that
 * lied goes with it. See `cellKey` in `analysis/anatomy.ts`.
 *
 * Three rules, in order, and each one is the fallback for the last:
 *
 *  1. **The text stack**, because the language model is the backbone and is what
 *     nearly everyone opening a multimodal checkpoint came to look at.
 *  2. **The first real stack**, when no stack announced a modality. On a
 *     Llava-shaped checkpoint the text stack sits at the bare `model.` root and
 *     names no modality at all, and `rank()` above already puts unrecognized
 *     stacks first for exactly that reason — so "first" is the backbone here too,
 *     by the same argument, rather than by luck.
 *  3. **Whatever is left**, which can only be the synthetic `shared` bucket, on
 *     the pathological model whose every stack was adopted into it. Opening on
 *     an empty grid would be worse than opening on the projector.
 *
 * `undefined` for a single-stack model: there are no towers, there is no filter,
 * and the whole inventory is the answer.
 */
export function defaultTower(towers: readonly Tower[]): Tower | undefined {
  if (towers.length === 0) return undefined;
  return (
    towers.find((tower) => tower.modality === "text") ??
    towers.find((tower) => tower.id !== SHARED_LABEL) ??
    towers[0]
  );
}

export function towerOf(name: string, towers: readonly Tower[]): string | undefined {
  if (towers.length === 0) return undefined;

  let best: Tower | undefined;
  for (const tower of towers) {
    if (tower.root === "" || !name.startsWith(tower.root)) continue;
    if (best === undefined || tower.root.length > best.root.length) best = tower;
  }
  if (best) return best.id;

  const words = new Set(name.split(".").flatMap(wordsOf));
  for (const tower of towers) {
    if (tower.modality === undefined) continue;
    const modality = MODALITIES.find((m) => m.label === tower.modality);
    if (modality?.words.some((word) => words.has(word))) return tower.id;
  }

  const rooted = towers.find((t) => t.root === "" && t.id !== SHARED_LABEL);
  return rooted?.id ?? towers.find((t) => t.id === SHARED_LABEL)?.id;
}

/**
 * Which tower a view should open on: the selected tensor's, or the default.
 *
 * **A selection names its own tower, and that beats the default.** Reported
 * twice in two days (`46988b9f`, `9811b063`), both multimodal: a `?tensor=` deep
 * link naming a vision tensor opened the *text* tower and printed the text
 * stack's `bits/weight` beside a map the linked tensor was not in. The reporter
 * flagged `bpw_wrong` and was right — the figure was wrong for what they were
 * looking at.
 *
 * The rule (reviewer, 2026-08-23): a **selection** event aligns the tower to the
 * tensor; a **view** event never touches the selection. Only one caller may
 * reach this — `syncTower`'s unset-or-stale branch — so a tower a visitor
 * actually chose is never overridden.
 *
 * `present` is not optional politeness. `towerOf` is **total**: it falls back to
 * the rooted or shared tower for any string at all, so without a check that the
 * tensor exists, `?tensor=junk` would silently steer which stack the page opens
 * on. Passed as a predicate rather than an inventory to keep this module free of
 * the reader's types.
 */
export function openingTower(
  selected: string | undefined,
  towers: readonly Tower[],
  present: (name: string) => boolean,
): string | undefined {
  if (towers.length === 0) return undefined;
  if (selected !== undefined && present(selected)) {
    const home = towerOf(selected, towers);
    if (home !== undefined) return home;
  }
  return defaultTower(towers)?.id;
}
